import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";

/**
 * completion - the authenticated-self completion read (W3-F1, brief section 5).
 *
 * It backs `GET api/v1/me/completion?challenge={id}` (W3-B1), which answers for
 * the CALLER ONLY: the server-controlled enablement flag, the caller's check-in
 * timestamps, the three door facts, and the caller's own-words excerpt when
 * that feature is enabled. It ships disabled and the read says so.
 *
 * WHY THIS IS A SECOND SLICE AND NOT A FIELD ON THE PROJECTION. The experience
 * projection is SQUAD-SHARED and Wave 2 requires every authorized caller of a
 * squad to receive an identical payload (brief section 3 item 1). Every
 * per-caller fact in this wave lives here instead, so the rule that made the
 * projection safe to share is not quietly weakened by one convenient field.
 *
 * IDENTITY IS `(userId, challengeId)` AND THE SCOPE KEY IS HOW IT IS ENFORCED.
 * `pending` clears the held payload the moment the scope changes, so a learner
 * switching account or challenge on one tab never sees the prior scope's doors
 * or the prior learner's sentence. The take-latest `requestId` covers the case
 * the scope key cannot: two overlapping reads for the SAME scope, where both
 * carry the same arg and only order separates them.
 *
 * `enabled` IS FALSE UNTIL A MATCHING SUCCESS, AND THAT IS THE FLAG CONTRACT
 * (brief section 3 item 11). The frontend renders the sequence only when the
 * SERVER says `enabled: true`; `REACT_APP_COMPLETION_SEQUENCE` is a bundling
 * boundary and never an enablement. So the initial value, the value under a
 * scope change, and the value after any failure are all false: there is no
 * state of this slice in which a sequence renders because the read has not
 * answered yet.
 *
 * A 403 CLEARS RATHER THAN RECORDS. The read returns one uniform 403 to any
 * caller without active membership in the challenge's cohort (staff, an exited
 * member, an impersonation view), which is D7's "never plays the sequence".
 * Holding the prior payload under a 403 would keep a settled record on screen
 * for a caller the server has just refused.
 */

let completionRequestSeq = 0;

/** The scope a held payload belongs to. Empty ids never produce a key. */
export const completionScopeKey = ({ userId, challengeId } = {}) =>
  userId && challengeId ? `${userId}:${challengeId}` : null;

/**
 * @param {object} arg
 * @param {number|string} arg.userId the authenticated learner. It is NOT sent
 *   to the server -- the route reads `auth('api')->user()` and returns 422 for
 *   a `user_id` parameter -- it is the client half of the scope key, so the
 *   reducers can tell a response for the previous account from one for this.
 *   Unlike the clearance read there is exactly ONE dispatcher of this thunk, so
 *   carrying it in the arg costs one call site rather than seven.
 * @param {number|string} arg.challengeId
 * @param {AbortSignal} [arg.signal]
 */
export const fetchCompletion = createAsyncThunk(
  "completion/fetch",
  async ({ challengeId, signal } = {}, thunkAPI) => {
    try {
      const response = await axiosInstance(
        "/me/completion",
        "get",
        undefined,
        // `params` and `signal` are promoted out of customHeaders to top-level
        // axios config by axiosInstance; passing the challenge as a header
        // would reach the server as a header and the FormRequest would reject
        // the request for a missing `challenge`.
        {
          params: { challenge: challengeId },
          signal: signal ?? thunkAPI.signal,
        },
      );
      return response.data.data;
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError")
        throw err;
      return thunkAPI.rejectWithValue({
        status: err?.response?.status ?? null,
        message: "Failed to load completion.",
      });
    }
  },
  { idGenerator: () => `cmp-${++completionRequestSeq}` },
);

/**
 * The shown acknowledgement for the own-words beat.
 *
 * IT IS FIRE-AND-FORGET BY DESIGN AND ITS FAILURE IS NOT THE LEARNER'S PROBLEM.
 * The row it writes is a research record, not a fact the screen depends on: the
 * beat has already rendered by the time this is dispatched, so a rejection must
 * not change anything the learner can see. It has no reducer arms for exactly
 * that reason, and the thunk swallows its own rejection rather than surfacing
 * one nothing consumes.
 *
 * `dwellMs` is the client's measured on-screen time of the beat, capped server
 * side at 60000 and nulled when the document was hidden. The cap is asserted
 * here as well so a runaway clock never leaves the client at all.
 */
export const DWELL_CAP_MS = 60000;

export const acknowledgeOwnWordsShown = createAsyncThunk(
  "completion/ownWordsShown",
  async ({ selectionId, dwellMs = null } = {}) => {
    if (!selectionId) return null;
    const capped =
      typeof dwellMs === "number" && Number.isFinite(dwellMs) && dwellMs >= 0
        ? Math.min(Math.round(dwellMs), DWELL_CAP_MS)
        : null;
    try {
      await axiosInstance(
        `/me/completion/own-words/${selectionId}/shown`,
        "post",
        capped === null ? {} : { dwell_ms: capped },
      );
    } catch {
      /**
       * FIRE AND FORGET IS NOT FAIL AND FORGET (fable-F1-m5).
       *
       * Nothing the learner sees depends on this row, which is why it has no
       * reducer arms and why a rejection changes nothing on screen. But the row
       * is the EXPOSURE INSTRUMENT the reactivity review's comparison group is
       * built from (reactivity C5): a dropped acknowledgement is a hole in the
       * bias analysis, and a silently dropped one is a hole nobody can find. A
       * failed write and a successful one must not be the same observation.
       *
       * The client has no dedicated channel, so this is a console warning with
       * a fixed prefix that can be grepped, and the selection id so a missing
       * row can be matched to it. The backend records `served` independently,
       * which is what makes the two counts comparable at all.
       */
      // eslint-disable-next-line no-console
      console.warn(
        `[completion] own words shown acknowledgement failed (selection ${selectionId})`,
      );
    }
    return null;
  },
);

const initialState = {
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  /**
   * HOW MANY READS HAVE STARTED, ever, for this slice.
   *
   * The completion boundary re-reads (fable-F1-M4) and the challenge form waits
   * for THAT read rather than for any success, so it needs to tell an answer
   * taken after the submission from the one that was already on screen.
   * `succeeded` alone cannot: the held answer is already `succeeded` at the
   * instant the boundary fires. A monotonic counter bumped when a read STARTS
   * is what makes "newer than the boundary" expressible, and it is the same
   * instrument, with the same reasoning, that `experienceProjection` uses.
   */
  generation: 0,
  error: null,
  errorStatus: null,
  requestId: null,
  scopeKey: null,
  /** The server's flag. FALSE until a matching success says otherwise. */
  enabled: false,
  challengeId: null,
  checkIns: [],
  doors: null,
  ownWords: null,
};

/**
 * Should the ONE Dashboard effect dispatch the completion read right now?
 *
 * Pure, so the decision is table-tested rather than reachable only through the
 * component, exactly as `shouldFetchProjection` is. `force` is the completion
 * boundary and the credential terminal transition: both are events after which
 * the held answer is known to be out of date, and neither is a scope change, so
 * without it a re-read after a submission would be indistinguishable from a
 * re-render.
 */
export const shouldFetchCompletion = ({
  userId,
  challengeId,
  status,
  scopeKey,
  force = false,
}) => {
  if (!userId || !challengeId) return false;
  if (status === "loading") return false;
  const nextScopeKey = completionScopeKey({ userId, challengeId });
  if (status === "idle" || scopeKey !== nextScopeKey) return true;
  return force === true;
};

const completionSlice = createSlice({
  name: "completion",
  initialState,
  reducers: {
    /** Explicit teardown when the surface family is left entirely. */
    clearCompletion: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompletion.pending, (state, action) => {
        const nextScopeKey = completionScopeKey(action.meta.arg);
        if (nextScopeKey !== state.scopeKey) {
          // Scope switched. Drop the prior learner's or the prior challenge's
          // answer NOW rather than when the new one lands: the gap between the
          // two is where a settled record for somebody else would render.
          state.enabled = false;
          state.challengeId = null;
          state.checkIns = [];
          state.doors = null;
          state.ownWords = null;
          state.error = null;
          state.errorStatus = null;
          state.scopeKey = nextScopeKey;
        }
        state.requestId = action.meta.requestId;
        state.status = "loading";
        // The read has STARTED. See `generation`: the bump belongs here rather
        // than on the settle, so that a wait opened at the boundary cannot be
        // satisfied by a read that had already begun.
        state.generation += 1;
      })
      .addCase(fetchCompletion.fulfilled, (state, action) => {
        if (completionScopeKey(action.meta.arg) !== state.scopeKey) return;
        if (action.meta.requestId !== state.requestId) return;
        const data = action.payload ?? {};
        state.status = "succeeded";
        state.error = null;
        state.errorStatus = null;
        // EVERY FIELD IS READ THROUGH A DEFAULT, and the defaults are the
        // withheld answers rather than convenient ones: a payload missing
        // `enabled` renders nothing, and a payload missing `doors` offers no
        // door. The fixture carries both shapes; a server that stopped carrying
        // a field is a contract break, and the contract test is where that is
        // caught, not a screen that invented a value for it.
        state.enabled = data.enabled === true;
        state.challengeId = data.challenge_id ?? null;
        state.checkIns = Array.isArray(data.check_ins) ? data.check_ins : [];
        state.doors = data.doors ?? null;
        state.ownWords = data.own_words ?? null;
      })
      .addCase(fetchCompletion.rejected, (state, action) => {
        if (action.meta.aborted) return;
        if (
          action.error?.name === "CanceledError" ||
          action.error?.name === "AbortError"
        ) {
          return;
        }
        if (completionScopeKey(action.meta.arg) !== state.scopeKey) return;
        if (action.meta.requestId !== state.requestId) return;
        state.status = "failed";
        state.error = action.payload?.message ?? "Failed to load completion.";
        state.errorStatus = action.payload?.status ?? null;
        /**
         * A REFUSAL CLEARS. A FAILURE DOES NOT.
         *
         * The uniform 403 is what a caller without active membership gets, and
         * it is also what an impersonation view gets, so holding the previous
         * payload through one would leave a record on screen for somebody the
         * server has just declined to answer. 401 is the same answer from a
         * session that has ended. Both are the server saying "not you".
         *
         * EVERY OTHER FAILURE KEEPS THE HELD PAYLOAD, and the difference is not
         * cosmetic (fable-F1-m4). Section 5 says "cleared on scope change and
         * on 403"; clearing on all of them meant a transient 5xx or a dropped
         * connection set `enabled` false, and `enabled` is what every surface
         * in this wave is mounted on. Once the completion boundary re-reads
         * (M4), a network blip mid-sequence would have unmounted a playing
         * sequence: the hold flushes, the rail advances, no beats. The learner
         * loses the moment because a request timed out.
         *
         * `status: "failed"` still says so, which is what the boundary's own
         * wait reads to stop waiting and proceed on what it holds.
         */
        const refused = state.errorStatus === 403 || state.errorStatus === 401;
        if (refused) {
          state.enabled = false;
          state.checkIns = [];
          state.doors = null;
          state.ownWords = null;
        }
      });
  },
});

export const { clearCompletion } = completionSlice.actions;

export default completionSlice.reducer;
